import { useCallback, useState } from 'react'
import firebase from '../../firebase/compat'
import { client } from '../../hooks/Client'
import { useFirestoreOrderBy } from '../../firebase/useFirestore'
import StorytellingMediaPreview from './StorytellingMediaPreview'
import StorytellingPhotoZoom from './StorytellingPhotoZoom'
import { STORYTELLING_KIND_LABELS, activityNameById } from '../../helpers/storytellingHelpers/storytellingKinds'

// De tab 'Te beoordelen': wat via een storytelling-deellink binnenkwam.
// Goedkeuren zet het in Media, Quotes of Anekdotes (de server kopieert het, in
// een transactie); afwijzen houdt het record bij en verwijdert een bestand.
// Een quote of anekdote kun je vóór het goedkeuren aanpassen.
// AI-CONTEXT: See docs/features/storytelling-deellink.md

const RETRY = 'Dat lukte niet. Probeer het nog eens.'

/** Waar een goedgekeurde inzending van elke soort te vinden is (de tabs van Storytelling.jsx). */
const TARGET_TABS = {
    quote: { id: 'quotes', label: 'Quotes' },
    anecdote: { id: 'anecdotes', label: 'Anekdotes' },
    media: { id: 'media', label: 'Media' },
}

/**
 * @param {number} ms
 * @returns {string} dd-mm-jjjj uu:mm
 */
const formatMoment = (ms) => {
    const d = new Date(ms)
    const two = (n) => String(n).padStart(2, '0')
    return `${two(d.getDate())}-${two(d.getMonth() + 1)}-${d.getFullYear()} ${two(d.getHours())}:${two(d.getMinutes())}`
}

/**
 * @param {object} props
 * @param {Array<import('../../hooks/useStorytellingSubmissions').StorytellingSubmissionRow> | null} props.submissions
 *   - De wachtende inzendingen; null zolang het niet bekend is
 * @param {boolean} props.failed - Het laden van de lijst is mislukt
 * @param {(id: string) => void} props.onDecided - Haalt een beoordeelde inzending uit de lijst (ook uit de teller in de tab)
 * @param {(tabId: string) => void} props.onOpenTab - Opent de tab waar een goedgekeurde inzending nu staat
 */
const StorytellingReviewTab = ({ submissions, failed, onDecided, onOpenTab }) => {
    const activities = useFirestoreOrderBy('Activities', 'Position', 'asc')

    // Aanpassingen per inzending, op id: een aanpassing reist zo nooit mee
    // naar een andere inzending, ook niet als de lijst opnieuw geladen wordt.
    // AI-CONTEXT: See docs/pitfalls.md#state-dialog-draft-outlives-its-target
    const [drafts, setDrafts] = useState(/** @type {Record<string, {text: string, name: string}>} */ ({}))
    // Welke knop bezig is: de beslissing duurt soms seconden (testronde 2026-10-30, B4).
    const [busy, setBusy] = useState(/** @type {{id: string, decision: 'approve'|'reject'} | null} */ (null))
    const busyId = busy ? busy.id : ''
    const [confirmRejectId, setConfirmRejectId] = useState('')
    // De foto die over het hele venster staat; '' = geen.
    const [zoomUrl, setZoomUrl] = useState('')
    const closeZoom = useCallback(() => setZoomUrl(''), [])
    const [problems, setProblems] = useState(/** @type {Record<string, string>} */ ({}))
    // Wat er met de laatst beoordeelde inzending gebeurde; blijft staan als de lijst daarna leeg is.
    const [done, setDone] = useState(/** @type {{kind: string, decision: 'approve'|'reject'} | null} */ (null))

    const target = done && done.decision === 'approve' ? TARGET_TABS[done.kind] : null
    const notice = done && (
        <div className="storytelling-review-done" role="status">
            <span>{target ? `Goedgekeurd. Hij staat nu bij ${target.label}.` : 'Afgewezen.'}</span>
            {target && <button type="button" onClick={() => onOpenTab(target.id)}>{`Naar ${target.label}`}</button>}
        </div>
    )

    if (failed) return <p className="storytelling-review-problem" role="alert">De inzendingen konden niet geladen worden.</p>
    if (submissions === null) return <div className="storytelling-review" aria-busy="true" />
    if (submissions.length === 0) {
        return (
            <div className="storytelling-review">
                {notice}
                <p className="storytelling-review-empty">Er wacht niets op beoordeling.</p>
            </div>
        )
    }

    /** @param {object} s */
    const draftOf = (s) => drafts[s.id] || { text: s.text, name: s.name }

    /** @param {string} id @param {{text?: string, name?: string}} patch @param {object} s */
    const editDraft = (id, patch, s) => setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || { text: s.text, name: s.name }), ...patch } }))

    /**
     * @param {object} s - De inzending
     * @param {'approve'|'reject'} decision
     */
    const decide = async (s, decision) => {
        if (busyId) return
        setBusy({ id: s.id, decision })
        setConfirmRejectId('')
        setProblems((prev) => ({ ...prev, [s.id]: '' }))
        const payload = { client, id: s.id, decision }
        if (decision === 'approve' && s.kind !== 'media') payload.edits = draftOf(s)
        try {
            await firebase.app().functions('europe-west1').httpsCallable('reviewStorytellingSubmission')(payload)
            setDone({ kind: s.kind, decision })
            onDecided(s.id)
        } catch (error) {
            console.error('StorytellingReviewTab: beoordelen mislukt', error)
            // De server zegt het zelf als de inzending al beoordeeld is, het bestand
            // weg is, of een aanpassing niet mag (leeg, te lang).
            const own = error?.code === 'failed-precondition' || error?.code === 'invalid-argument'
            setProblems((prev) => ({ ...prev, [s.id]: own ? error.message : RETRY }))
        }
        setBusy(null)
    }

    /** @param {string} id @param {'approve'|'reject'} decision @param {string} label */
    const buttonLabel = (id, decision, label) => (busy && busy.id === id && busy.decision === decision ? 'Bezig…' : label)

    return (
        <div className="storytelling-review">
            {notice}
            {zoomUrl && <StorytellingPhotoZoom src={zoomUrl} alt="Ingestuurde foto" onClose={closeZoom} />}
            {submissions.map((s) => {
                const draft = draftOf(s)
                return (
                    <div key={s.id} className="storytelling-review-item" data-testid={`storytelling-review-${s.id}`}>
                        <div className="storytelling-review-meta">
                            <span className="storytelling-review-kind">{STORYTELLING_KIND_LABELS[s.kind]}</span>
                            {s.created && <span>{formatMoment(s.created)}</span>}
                            {s.activity.map((id) => activityNameById(activities, id)).filter(Boolean).map((naam) => <span key={naam}>{naam}</span>)}
                        </div>

                        {s.kind === 'media' ? (
                            <>
                                {s.previewUrl && (String(s.contentType).startsWith('video/')
                                    // Een video heeft in zijn eigen bediening al een knop voor volledig scherm.
                                    ? <StorytellingMediaPreview className="storytelling-review-media" src={s.previewUrl} type={s.contentType} />
                                    : (
                                        <button type="button" className="storytelling-review-zoom" aria-label="Foto schermvullend bekijken" title="Klik om de foto schermvullend te bekijken" onClick={() => setZoomUrl(s.previewUrl)}>
                                            <StorytellingMediaPreview className="storytelling-review-media" src={s.previewUrl} type={s.contentType} alt="Ingestuurde foto" />
                                        </button>
                                    ))}
                                {s.name && <p className="storytelling-review-name">{s.name}</p>}
                            </>
                        ) : (
                            <>
                                <label className="storytelling-review-field">
                                    <span>Tekst</span>
                                    <textarea value={draft.text} rows={s.kind === 'quote' ? 3 : 8} onChange={(e) => editDraft(s.id, { text: e.target.value }, s)} />
                                </label>
                                <label className="storytelling-review-field">
                                    <span>Naam</span>
                                    <input type="text" value={draft.name} onChange={(e) => editDraft(s.id, { name: e.target.value }, s)} />
                                </label>
                            </>
                        )}

                        {problems[s.id] && <p className="storytelling-review-problem" role="alert">{problems[s.id]}</p>}

                        {confirmRejectId === s.id ? (
                            <div className="storytelling-review-actions">
                                <p>{s.kind === 'media' ? 'Weet je het zeker? Het bestand wordt verwijderd.' : 'Weet je het zeker?'}</p>
                                <button type="button" disabled={Boolean(busyId)} onClick={() => decide(s, 'reject')}>Ja, afwijzen</button>
                                <button type="button" onClick={() => setConfirmRejectId('')}>Annuleren</button>
                            </div>
                        ) : (
                            <div className="storytelling-review-actions">
                                <button type="button" disabled={Boolean(busyId)} onClick={() => decide(s, 'approve')}>{buttonLabel(s.id, 'approve', 'Goedkeuren')}</button>
                                <button type="button" disabled={Boolean(busyId)} onClick={() => setConfirmRejectId(s.id)}>{buttonLabel(s.id, 'reject', 'Afwijzen')}</button>
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export default StorytellingReviewTab
